import { FormControl, Radio, RadioGroup } from '@mui/material';
import { Select, useLocale } from 'shared-ui';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';

import { AppMessages } from 'i18n/messages';
import { useAppSelectOptions } from 'hooks/useAppSelectOptions/useAppSelectOptions';
import { TreeOption, TreeSelectOption } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect.types';
import { YesNoFiltersEnum } from 'app/events/eventsFilters/EventsFilters.enum';
import { OrganizationsTreeSelect } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect';
import { UserPermissionsForm } from '../UserPermissions.types';
import * as Styled from '../UserPermissions.styles';

import { UserDataPermissionsProps } from './UserDataPermissions.types';

export const UserDataPermissions = ({ organizations }: UserDataPermissionsProps) => {
  const { formatMessage } = useLocale();
  const { yesNoOptions } = useAppSelectOptions();

  const { watch, setValue } = useFormContext<UserPermissionsForm>();

  const accessAllData = watch('accessAllData');
  const accessFeaturedCards = watch('accessFeaturedCards');
  const accessNoOrganizations = watch('accessNoOrganizations');
  const selectedOrganizations = watch('selectedOrganizations');

  const selectedTreeOptions: TreeSelectOption[] = useMemo(() => {
    const findOrgById = (orgs: TreeOption[], id: string): TreeSelectOption | null => {
      for (const org of orgs) {
        if (org.id === id) {
          return { id: org.id, nazwa: org.nazwa, kod: org.kod };
        }
        if (org.children) {
          const found = findOrgById(org.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    return selectedOrganizations.map((id) => findOrgById(organizations, id)).filter(Boolean) as TreeSelectOption[];
  }, [selectedOrganizations, organizations]);

  const isFeaturedCardsDisabled = accessAllData;

  const isLimitedAccessDisabled = accessAllData;

  const isAccessNoOrganizationsDisabled = accessAllData;

  const handleAccessAllDataChange = (value: boolean) => {
    setValue('accessAllData', value, { shouldValidate: true, shouldDirty: true });

    if (value) {
      setValue('accessFeaturedCards', true, { shouldValidate: true, shouldDirty: true });
      setValue('accessNoOrganizations', true, { shouldValidate: true, shouldDirty: true });
      setValue('selectedOrganizations', [], { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleFeaturedCardsChange = (value: boolean) => {
    if (!isFeaturedCardsDisabled) {
      setValue('accessFeaturedCards', value, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleOrganizationsChange = (options: TreeSelectOption[] | undefined) => {
    if (!isLimitedAccessDisabled) {
      const selectedIds = options ? options.map((option) => option.id) : [];
      setValue('selectedOrganizations', selectedIds, { shouldValidate: true, shouldDirty: true });
    }
  };

  return (
    <Styled.Section>
      <Styled.DataPermissionsContainer>
        <FormControl component="fieldset">
          <Styled.DataPermissionsTitle>
            {formatMessage({ id: AppMessages['permissions.data.allAccess'] })}
          </Styled.DataPermissionsTitle>
          <RadioGroup row value={accessAllData} onChange={(e) => handleAccessAllDataChange(e.target.value === 'true')}>
            <Styled.FormControlLabel
              value={true}
              control={<Radio />}
              label={formatMessage({ id: AppMessages['common.yes'] })}
            />
            <Styled.FormControlLabel
              value={false}
              control={<Radio />}
              label={formatMessage({ id: AppMessages['common.no'] })}
            />
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset" disabled={isFeaturedCardsDisabled}>
          <Styled.DataPermissionsTitle>
            {formatMessage({ id: AppMessages['permissions.data.highlightedCards'] })}
          </Styled.DataPermissionsTitle>
          <RadioGroup
            row
            value={accessFeaturedCards}
            onChange={(e) => handleFeaturedCardsChange(e.target.value === 'true')}
          >
            <Styled.FormControlLabel
              value={true}
              control={<Radio />}
              label={formatMessage({ id: AppMessages['common.yes'] })}
            />
            <Styled.FormControlLabel
              value={false}
              control={<Radio />}
              label={formatMessage({ id: AppMessages['common.no'] })}
            />
          </RadioGroup>
        </FormControl>

        <Styled.SelectFieldsContainer>
          <div>
            <OrganizationsTreeSelect
              multiple
              disabled={isLimitedAccessDisabled}
              value={isLimitedAccessDisabled ? [] : selectedTreeOptions}
              onChange={handleOrganizationsChange}
              label={formatMessage({ id: AppMessages['permissions.data.limitedCellsAccess'] })}
              placeholder={formatMessage({ id: AppMessages['common.list.placeholder'] })}
              inheritChildren
            />

            {!accessAllData && selectedOrganizations.length === 0 && (
              <Styled.FormHelperText error>
                {formatMessage({ id: AppMessages['permissions.data.limitedCellsAccess.required'] })}
              </Styled.FormHelperText>
            )}
          </div>

          <Select
            label={formatMessage({ id: AppMessages['permissions.data.noCellsAccess'] })}
            options={yesNoOptions}
            value={accessNoOrganizations ? YesNoFiltersEnum.YES : YesNoFiltersEnum.NO}
            onChange={(event) => {
              setValue('accessNoOrganizations', event.target.value === YesNoFiltersEnum.YES, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            disabled={isAccessNoOrganizationsDisabled}
          />
        </Styled.SelectFieldsContainer>
      </Styled.DataPermissionsContainer>
    </Styled.Section>
  );
};
